import React, { useEffect, useRef, useState } from 'react';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import FadeInSection from './FadeInSection';
import './fadeAnimation.css'; // CSS para as animações
import './slimpass-colors.css'; // Cores do SlimPass
import './print.css'; // Estilos para impressão
import { AbaId, abaInicial, rotulosAbas, temAbasPeriodo } from './abasPeriodo';

// URL do logo SlimPass para o rodapé
const slimpassLogoUrl = "/images/Logo+slogan_light.png";

// Definindo interface para props do Dashboard
interface DashboardProps {
  operadoraId: string;
  data: any;
  dataPeriodoNovo?: any;
  headerImageUrl: string;
  onLogout: () => void;
  onChangeOperadora: () => void;
  onUrlMaskChange: (operadoraId: string, shouldMask: boolean) => void;
}

// Definir interfaces para os tipos de dados usados
interface CategoriaItem {
  nome: string;
  valor: number;
  percentual: number;
}

interface ComorbidadeItem {
  nome: string;
  valor: number;
  percentual: number;
  grupoId?: string;
}

interface GrupoComorbidade {
  id: string;
  nome: string;
  comorbidades: string[];
}

interface ComorbidadeChartItem {
  nome: string;
  quantidade: number;
  percentagem: number;
}

interface PieDataItem {
  name: string;
  value: number;
}

interface HidratacaoItem {
  faixa: string;
  valor: number;
  percentual: number;
}

interface RespostasData {
  total: number;
  validadas: number;
  percentualValidadas: number;
}

interface BaseUtilizada {
  baseUtilizada: "validada" | "completa";
}

interface PesoCritico {
  valor: number;
  descricao: string;
}

interface Metadata {
  duasBasesUtilizadas: boolean;
  descricaoBaseValidada: string;
  descricaoBaseCompleta: string;
  dataAtualizacao: string;
  versaoDados: string;
}

// Cores para os gráficos
const COLORS = {
  primary: ['#57b85b', '#ffad33', '#f76056'],
  secondary: ['#5067c1', '#7180c8', '#93a0d5', '#b3bde3'],
  red: ['#e85551', '#f27270', '#f76056', '#eb8c8c'],
  green: ['#54b159', '#7ac97d', '#57b85b', '#99d39b'],
  neutral: ['#596a73', '#7691a0', '#8fa7b1', '#a8bac5'],
  chart: [
    '#ffb84d', '#5067c1', '#e85551', '#54b159', '#ff7a4d', 
    '#ab4dbc', '#4da6ff', '#f76056', '#57b85b', '#ffcf4d', 
    '#26a69a', '#8059c9', '#fff066', '#d6e866', '#8c6b5a'
  ]
};

// Dados das comorbidades
const saudeMentalData = [
  { nome: 'Ansiedade', quantidade: 340, percentagem: 53.8 },
  { nome: 'Depressão', quantidade: 85, percentagem: 13.4 },
  { nome: 'Bipolaridade', quantidade: 34, percentagem: 5.3 }
];

const doencasMetabolicasData = [
  { nome: 'Diabetes tipo 2', quantidade: 86, percentagem: 13.6 },
  { nome: 'Colesterol alto', quantidade: 157, percentagem: 24.8 },
  { nome: 'Pressão arterial sistêmica', quantidade: 190, percentagem: 30.1 },
  { nome: 'Gordura no fígado', quantidade: 180, percentagem: 28.5 },
  { nome: 'Hipotireoidismo', quantidade: 66, percentagem: 10.4 },
  { nome: 'Infertilidade', quantidade: 14, percentagem: 2.1 }
];

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-4 border rounded shadow">
        <p className="font-semibold">{label}</p>
        <p className="text-blue-600">Quantidade: {payload[0].value}</p>
        <p className="text-green-600">Percentagem: {payload[1].value}%</p>
      </div>
    );
  }
  return null;
};

const ComorbidadesChart = ({ data, title, colorGroup }: { data: any[], title: string, colorGroup: string[] }) => {
  const chartMinWidth = Math.max(500, data.length * 60 + 300);
  return (
    <div className="bg-gray-50 rounded-lg py-4 sm:py-6 px-1 mb-6 shadow-md">
      <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4 sm:mb-6 text-center">{title.replace(/^Grupo \d+: /, '')}</h3>
      <div className="overflow-x-auto -mx-1">
        <div style={{ minWidth: `${chartMinWidth}px` }} className="h-[350px] sm:h-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              layout="vertical"
              margin={{ top: 5, right: 100, left: 10, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" domain={[0, 'dataMax']} />
              <YAxis
                dataKey="nome"
                type="category"
                width={140}
                tick={{ fontSize: 13 }}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    const item = payload[0].payload;
                    return (
                      <div className="bg-white p-3 border rounded shadow text-sm">
                        <p className="font-semibold">{label}</p>
                        <p className="text-blue-800">Quantidade: {item.quantidade}</p>
                        <p className="text-green-800">Percentagem: {item.percentagem}%</p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Bar
                dataKey="quantidade"
                name="Quantidade e Percentagem"
                isAnimationActive={false}
                radius={[0, 8, 8, 0]}
                label={(props) => {
                  const { x, y, width, height, value, index } = props;
                  const item = data[index];
                  const formattedText = `${value} pessoas (${item.percentagem}%)`;

                  const estimatedTextWidth = formattedText.length * 8;
                  const textPadding = 20;
                  const fitsInside = width > (estimatedTextWidth + textPadding);

                  if (!fitsInside) {
                    return (
                      <text
                        x={x + width + 5}
                        y={y + height / 2}
                        fill="#000000"
                        textAnchor="start"
                        dominantBaseline="middle"
                        fontSize={14}
                        fontWeight="normal"
                      >
                        {formattedText}
                      </text>
                    );
                  }

                  return (
                    <text
                      x={x + width - 10}
                      y={y + height / 2}
                      fill="#FFFFFF"
                      textAnchor="end"
                      dominantBaseline="middle"
                      fontSize={14}
                      fontWeight="bold"
                    >
                      {formattedText}
                    </text>
                  );
                }}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={colorGroup[index % colorGroup.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const StatsCard = ({ title, value, description, color }: { title: string, value: string, description: string, color: string }) => (
  <div className={`bg-white rounded-xl shadow-md p-6 border-t-4 border-${color}-500`}>
    <p className="text-sm font-medium text-gray-500">{title}</p>
    <p className={`text-3xl font-bold mt-2 text-${color}-600`}>{value}</p>
    <p className="text-sm text-gray-600 mt-2">{description}</p>
  </div>
);

const Dashboard: React.FC<DashboardProps> = ({ operadoraId, data, dataPeriodoNovo, headerImageUrl, onLogout, onChangeOperadora, onUrlMaskChange }) => {
  const [hideOperatorInfo, setHideOperatorInfo] = useState(false);
  const [selectedComorbidadeGrupo, setSelectedComorbidadeGrupo] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<AbaId>(abaInicial(operadoraId));
  
  const toggleOperatorInfoVisibility = () => {
    const newHideState = !hideOperatorInfo;
    setHideOperatorInfo(newHideState);
    
    // Atualizar URL baseado no estado de ocultação
    onUrlMaskChange(operadoraId, newHideState);
  };

  const handlePrint = () => {
    window.print();
  };
  
  const temAbas = temAbasPeriodo(operadoraId, dataPeriodoNovo);
  const currentData = temAbas && activeTab === 'periodo-novo' ? dataPeriodoNovo : data;
  const { anterior: labelAbaAnterior, novo: labelAbaNova } = temAbas
    ? rotulosAbas(operadoraId, data, dataPeriodoNovo)
    : { anterior: '', novo: '' };
  
  // Obter o número total de respostas (dependendo da estrutura dos dados)
  const totalRespostas = currentData.respostas?.total || currentData.respostas;
  const respostasValidadas = currentData.respostas?.validadas || currentData.respostas;
  
  // Calcular percentual acima do peso ideal (sobrepeso + obesidade)
  const calcularPercentualAcimaDoPesoIdeal = () => {
    const categorias = currentData.imc.categorias;
    
    // Encontrar categorias de sobrepeso e obesidade
    const sobrepesoItem = categorias.find((cat: CategoriaItem) => 
      cat.nome.toLowerCase().includes("sobrepeso") || 
      (cat.nome.toLowerCase().includes("imc") && cat.nome.includes("25-30"))
    );
    
    const obesidadeItem = categorias.find((cat: CategoriaItem) => 
      cat.nome.toLowerCase().includes("obesidade") || 
      (cat.nome.toLowerCase().includes("imc") && cat.nome.includes("> 30"))
    );
    
    const percentualSobrepeso = sobrepesoItem?.percentual || 0;
    const percentualObesidade = obesidadeItem?.percentual || 0;
    
    return (percentualSobrepeso + percentualObesidade).toFixed(1).replace('.', ',');
  };
  
  // Percentual acima do peso ideal
  const percentualAcimaDoPesoIdeal = calcularPercentualAcimaDoPesoIdeal();
  
  // Preparar dados para o gráfico de IMC
  const imcPieData = currentData.imc.categorias
    .filter((cat: CategoriaItem) => cat.valor > 0) // Filtrar categorias com valor zero
    .map((cat: CategoriaItem) => ({
      name: cat.nome,
      value: cat.valor
    }));

  // Preparar dados para obesidade por graus
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const obesidadeGrausPieData = currentData.imc.obesidadeGraus.map((grau: CategoriaItem) => ({
    name: grau.nome,
    value: grau.valor
  }));

  // Preparar dados para o gráfico de comorbidades
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const comorbidadesData = [...currentData.comorbidades.tipos];
  
  // Filtrar comorbidades por grupo se um grupo estiver selecionado
  const filteredComorbidadesData = selectedComorbidadeGrupo 
    ? comorbidadesData.filter((item: ComorbidadeItem) => item.grupoId === selectedComorbidadeGrupo)
    : comorbidadesData;
    
  // Ordenar por valor decrescente
  const sortedComorbidadesData = [...filteredComorbidadesData].sort((a, b) => b.valor - a.valor);

  // Preparar dados para o gráfico de presença de comorbidades
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const comorbidadesPieData = [
    { name: 'Com pelo menos 1 comorbidade', value: currentData.comorbidades.comUma },
    { name: 'Sem comorbidades', value: currentData.comorbidades.semNenhuma }
  ];

  // Preparar dados para o gráfico de sono
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const sonoData = [
    { name: 'Menos de 7 horas', value: currentData.sono.percentualMenosDeSeteHoras },
    { name: 'Sono não reparador', value: currentData.sono.percentualNaoReparador },
    { name: 'Ronco noturno', value: currentData.sono.percentualRoncoNoturno },
    { name: 'Insônia', value: currentData.sono.percentualInsonia }
  ];

  // Dados para o gráfico de sedentarismo
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const sedentarismoData = [
    { name: 'Sedentários', value: currentData.sedentarismo.valor, percentual: currentData.sedentarismo.percentual },
    { name: 'Não Sedentários', value: totalRespostas - currentData.sedentarismo.valor, percentual: 100 - currentData.sedentarismo.percentual }
  ];
  
  // Preparar dados de hidratação
  const hidratacaoData = currentData.hidratacao.faixas || currentData.hidratacao;

  return (
    <div className="bg-gray-50 min-h-screen flex flex-col relative overflow-x-hidden">
      {/* Actions buttons - absolute position on top right */}
      <div className="absolute top-2 right-2 sm:top-4 sm:right-4 flex space-x-1 sm:space-x-2 z-10">
        <button 
          className="bg-white p-2 rounded-full shadow-md hover:bg-gray-100"
          onClick={onChangeOperadora}
          title="Trocar Operadora"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-6 sm:w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
        <button 
          className="bg-white p-2 rounded-full shadow-md hover:bg-gray-100 print-hide"
          onClick={handlePrint}
          title="Imprimir Relatório"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-6 sm:w-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
          </svg>
        </button>
        <button 
          className="bg-white p-2 rounded-full shadow-md hover:bg-gray-100"
          onClick={toggleOperatorInfoVisibility}
          title={hideOperatorInfo ? "Mostrar Dados da Operadora" : "Ocultar Dados da Operadora"}
        >
          <span role="img" aria-label="visibility toggle" style={{ fontSize: '1.25rem' }}>👁️</span>
        </button>
        <button 
          className="bg-white p-2 rounded-full shadow-md hover:bg-gray-100"
          onClick={onLogout}
          title="Sair"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-6 sm:w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
        </button>
      </div>

      <div className="p-3 sm:p-6 flex-grow flex justify-center">
        <div className="max-w-[980px] w-full">
          {/* Header */}
          <div className="w-full mb-8 overflow-hidden rounded-3xl shadow-md">
                          <img 
                src={hideOperatorInfo ? "/images/capa-form-diagnostico-empty.png" : headerImageUrl}
                alt={hideOperatorInfo ? "Dados Ocultados" : "Diagnóstico de Saúde"}
                className="w-full h-auto object-cover"
              />
              <div className="bg-white p-4 sm:p-8 text-center">
                <h1 className="text-xl sm:text-3xl font-bold text-gray-800">
                  Diagnóstico de Saúde Populacional {hideOperatorInfo ? "[Dados Ocultados]" : currentData.autogestora}
                </h1>
            </div>
          </div>
          
          {/* Sistema de Abas por Período */}
          <div className="w-full mb-6 sm:mb-8">
            <div className="flex border-b border-gray-200 overflow-x-auto">
              {temAbas ? (
                // Abas: período anterior + atualização (FUPS, PLAS/JMU) e estudo geral + PAMPA (Elosaúde)
                <>
                  <button
                    className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === 'periodo-anterior'
                        ? 'border-primary text-primary bg-blue-50'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                    onClick={() => setActiveTab('periodo-anterior')}
                  >
                    {labelAbaAnterior}
                  </button>
                  <button
                    className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === 'periodo-novo'
                        ? 'border-primary text-primary bg-blue-50'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                    onClick={() => setActiveTab('periodo-novo')}
                  >
                    {labelAbaNova}
                  </button>
                </>
              ) : (
                // Aba única para outras operadoras
                <button
                  className="px-6 py-3 text-sm font-medium border-b-2 border-primary text-primary bg-blue-50"
                >
                  Período de Coleta ({currentData.periodo})
                </button>
              )}
            </div>
          </div>
          
          {/* Resumo do Estudo */}
          <FadeInSection delay={60}>
            <div className="bg-white rounded-slimpass shadow-md p-4 sm:p-8 mb-6 sm:mb-8 print-avoid-break">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-6 sm:mb-10 header-accent">Resumo do Estudo</h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                                  <div className="bg-gray-50 rounded-lg p-4 text-center border border-gray-200">
                    <p className="text-sm text-gray-500 mb-1">Operadora</p>
                    <p className="text-lg font-bold">{hideOperatorInfo ? "[Dados Ocultados]" : currentData.autogestora}</p>
                    </div>
                  
                <div className="bg-gray-50 rounded-lg p-4 text-center border border-gray-200">
                  <p className="text-sm text-gray-500 mb-1">Período da coleta</p>
                  <p className="text-lg font-bold">{currentData.periodo}</p>
                  </div>
                  
                <div className="bg-gray-50 rounded-lg p-4 text-center border border-gray-200">
                  <p className="text-sm text-gray-500 mb-1">Total de respostas</p>
                  <p className="text-lg font-bold">
                    {currentData.respostas?.total || currentData.respostas}
                    {((operadoraId === 'cabergs' || operadoraId === 'casembrapa' || operadoraId === 'plas-jmu') && currentData.respostas?.percentualCarteira) && (
                      <span className="text-sm text-gray-600 ml-1">({currentData.respostas.percentualCarteira.toString().replace('.', ',')}% da carteira)</span>
                    )}
                  </p>
                  </div>
                </div>
              
              {/* Alerta para bases de dados */}
              {currentData.metadata?.duasBasesUtilizadas && (
                <div className="bg-blue-100 rounded-lg p-5 mb-6 border-2 border-blue-500 shadow-md text-center">
                  <h3 className="font-bold text-blue-800 mb-3 text-lg">⚠️ Atenção: Duas bases de dados utilizadas</h3>
                  <div className="flex flex-col items-center space-y-4">
                    <div className="text-blue-800">
                      <p className="font-bold">Base validada:</p>
                      <p>{currentData.respostas.validadas} respostas com carteirinhas validadas</p>
                      <p className="text-xs italic">(usada para análises de peso e IMC)</p>
                    </div>
                    <div className="text-blue-800">
                      <p className="font-bold">Base completa:</p>
                      <p>{currentData.respostas.total} respostas totais</p>
                      <p className="text-xs italic">(usada para análises de comorbidades, sono, etc.)</p>
                    </div>
                  </div>
                </div>
              )}
              
                              <p className="text-gray-600 mb-4 text-left">
                  Este relatório apresenta uma análise dos dados coletados na operadora <strong>{hideOperatorInfo ? "[Dados Ocultados]" : currentData.autogestora}</strong> durante o período de <strong>{currentData.periodo}</strong>, com um total de <strong>{currentData.respostas?.total || currentData.respostas}</strong> participantes{((operadoraId === 'cabergs' || operadoraId === 'casembrapa' || operadoraId === 'plas-jmu') && currentData.respostas?.percentualCarteira) && (
                    <span> (<strong>{currentData.respostas.percentualCarteira.toString().replace('.', ',')}% da carteira</strong>)</span>
                  )}.
                {operadoraId === 'fups' && activeTab === 'periodo-novo' && (
                  <span className="text-primary font-semibold"> Todos os dados apresentados são baseados exclusivamente em carteirinhas validadas.</span>
                )}
              </p>
              <p className="text-gray-600 mb-6 text-left">
                O diagnóstico compila informações sobre peso, IMC, comorbidades, sono, hábitos alimentares e atividade física dos participantes, oferecendo uma visão abrangente do perfil de saúde populacional.
              </p>
              
              {/* Agências - se disponível */}
              {currentData.agencias && (
                <div className="mt-4 bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h3 className="font-semibold text-gray-700 mb-2">Dados de Agências</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Total de agências</p>
                      <p className="text-xl font-bold">{currentData.agencias.total}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Pendência média por agência</p>
                      <p className="text-xl font-bold">{currentData.agencias.percentualPendente}%</p>
                    </div>
                  </div>
                  {currentData.agencias.possuiDetalhamento && (
                    <p className="text-sm text-gray-500 mt-2">* Detalhamento disponível em planilha separada</p>
                  )}
                </div>
              )}
            </div>
          </FadeInSection>

          {/* Perfil Demográfico (genérico: idade + situação) — plas-jmu usa o bloco "da Carteira" abaixo */}
          {currentData.demografia && currentData.demografia.idade && !currentData.demografia.tipoVinculo && (
            <FadeInSection delay={90}>
              <div className="grid grid-cols-1 gap-6 mb-6">
                <div className="bg-white rounded-slimpass shadow-md p-4 sm:p-6 print-avoid-break">
                  <h2 className="text-lg sm:text-xl font-semibold text-gray-800 header-accent mb-4">Perfil Demográfico</h2>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Idade Média */}
                    <div className={`bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-4 text-center shadow-sm border-2 border-indigo-400 ${!currentData.demografia.situacaoProfissional && !currentData.demografia.participacaoProgramaEmagrecimento ? 'md:col-span-3' : ''}`}>
                      <p className="text-gray-600 text-sm font-medium">Idade Média</p>
                      <p className="text-4xl font-bold text-primary my-2">{currentData.demografia.idade.medio.toString().replace('.', ',')}</p>
                      <p className="text-sm text-gray-600">variação: {currentData.demografia.idade.minimo} a {currentData.demografia.idade.maximo} anos</p>
                    </div>

                    {/* Situação Profissional */}
                    {currentData.demografia.situacaoProfissional && (
                      <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 text-center shadow-sm border-2 border-green-400">
                        <p className="text-gray-600 text-sm font-medium">Situação Profissional</p>
                        <p className="text-lg font-bold text-gray-800 my-1">{currentData.demografia.situacaoProfissional.ativos} ativos</p>
                        <p className="text-lg font-bold text-gray-800 my-1">{currentData.demografia.situacaoProfissional.aposentados} aposentados</p>
                        {currentData.demografia.situacaoProfissional.naoRespondeu > 0 && (
                          <p className="text-sm text-gray-500">{currentData.demografia.situacaoProfissional.naoRespondeu} não responderam</p>
                        )}
                      </div>
                    )}

                    {/* Participação em Programas */}
                    {currentData.demografia.participacaoProgramaEmagrecimento && (
                      <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 text-center shadow-sm border-2 border-purple-400">
                        <p className="text-gray-600 text-sm font-medium">Participação em Programas</p>
                        <p className="text-3xl font-bold text-primary my-2">{currentData.demografia.participacaoProgramaEmagrecimento.nuncaParticiparam}</p>
                        <p className="text-sm text-gray-600">{currentData.demografia.participacaoProgramaEmagrecimento.percentualNuncaParticiparam}% nunca participaram</p>
                      </div>
                    )}
                  </div>

                  {/* Informações extras apenas para impressão */}
                  <div className="hidden print:block mt-6 text-xs text-gray-600">
                    <p><strong>Idade média:</strong> Indicador do perfil etário da população estudada.</p>
                    <p><strong>Situação profissional:</strong> Distribuição entre ativos, aposentados e não respondentes.</p>
                    <p><strong>Participação em programas:</strong> Histórico de envolvimento em iniciativas de emagrecimento.</p>
                  </div>
                </div>
              </div>
            </FadeInSection>
          )}

          {/* Seções Especiais - Perfil Demográfico e Critério Estabelecido */}
          <FadeInSection delay={100}>
            <div className="grid grid-cols-1 gap-6 mb-6">
              {/* Critério Especial IMC ≥ 27 - Casembrapa */}
              {operadoraId === 'casembrapa' && currentData.criterioEstabelecido && (
                <div className="bg-white rounded-slimpass shadow-md p-6 print-avoid-break">
                  <h2 className="text-xl font-semibold text-gray-800 header-accent mb-4">Critério Estabelecido pela Operadora</h2>
                  
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 text-center shadow-sm border-2 border-blue-400">
                    <p className="text-lg text-gray-700 mb-2">
                      <strong>IMC ≥ 27</strong> - Critério de referência para intervenção
                    </p>
                    <div className="text-3xl font-bold text-blue-600">
                      {currentData.criterioEstabelecido.imcMaiorIgual27} pessoas
                    </div>
                    <div className="text-lg text-blue-700 font-semibold">
                      ({currentData.criterioEstabelecido.percentualImcMaiorIgual27.toFixed(1).replace('.', ',')}% dos participantes)
                    </div>
                    <p className="text-sm text-gray-600 mt-2">
                      Este é o grupo prioritário para ações de saúde da operadora
                    </p>
                  </div>
                </div>
              )}

              {/* Perfil Demográfico da Carteira (data-driven: qualquer operadora com situação % + vínculo — PLAS/JMU, CELOS) */}
              {currentData.demografia?.situacaoProfissional && currentData.demografia?.tipoVinculo && (
                <div className="bg-white rounded-slimpass shadow-md p-6 print-avoid-break">
                  <h2 className="text-xl font-semibold text-gray-800 header-accent mb-4">Perfil Demográfico da Carteira</h2>

                  {currentData.demografia.idade && (
                    <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-4 text-center shadow-sm border-2 border-indigo-400 mb-6">
                      <p className="text-gray-600 text-sm font-medium">Idade Média</p>
                      <p className="text-4xl font-bold text-primary my-2">{currentData.demografia.idade.medio.toString().replace('.', ',')}</p>
                      <p className="text-sm text-gray-600">variação: {currentData.demografia.idade.minimo} a {currentData.demografia.idade.maximo} anos</p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Situação Profissional */}
                    <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-4 text-center shadow-sm border-2 border-indigo-400">
                      <p className="text-gray-600 text-sm font-medium">Situação Profissional</p>
                      <div className="flex justify-center items-center space-x-8 mt-3">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-600">{currentData.demografia.situacaoProfissional.ativos}</div>
                          <div className="text-xs text-gray-600 font-medium">Ativos ({currentData.demografia.situacaoProfissional.percentualAtivos.toString().replace('.', ',')}%)</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-blue-600">{currentData.demografia.situacaoProfissional.aposentados}</div>
                          <div className="text-xs text-gray-600 font-medium">Aposentados ({currentData.demografia.situacaoProfissional.percentualAposentados.toString().replace('.', ',')}%)</div>
                        </div>
                      </div>
                    </div>

                    {/* Tipo de Vínculo */}
                    {currentData.demografia.tipoVinculo && (
                      <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-4 text-center shadow-sm border-2 border-indigo-400">
                        <p className="text-gray-600 text-sm font-medium">Tipo de Vínculo</p>
                        <div className="flex justify-center items-center space-x-8 mt-3">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-purple-600">{currentData.demografia.tipoVinculo.titulares}</div>
                            <div className="text-xs text-gray-600 font-medium">Titulares ({currentData.demografia.tipoVinculo.percentualTitulares.toString().replace('.', ',')}%)</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-orange-600">{currentData.demografia.tipoVinculo.dependentes}</div>
                            <div className="text-xs text-gray-600 font-medium">Dependentes ({currentData.demografia.tipoVinculo.percentualDependentes.toString().replace('.', ',')}%)</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </FadeInSection>

          {/* Perfil Antropométrico */}
          <FadeInSection delay={120}>
            <div className="grid grid-cols-1 gap-6 mb-6">
              <div className="bg-white rounded-slimpass shadow-md p-4 sm:p-6 print-avoid-break">
                <div className="mb-4 sm:mb-6">
                  <h2 className="text-lg sm:text-xl font-semibold text-gray-800 header-accent mb-2">Dados Gerais</h2>
                  {currentData.peso.baseUtilizada === "validada" && !(operadoraId === 'fups' && activeTab === 'periodo-novo') && (
                    <div className="bg-blue-50 rounded-lg px-3 py-1 text-center mt-2 mb-4 max-w-xs mx-auto">
                      <span className="text-sm font-normal text-blue-800">
                        Base validada: <b>{currentData.respostas.validadas}</b> pessoas
                      </span>
                    </div>
                  )}
                </div>
                
                <div className="mb-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-3 sm:p-5 text-center shadow-sm border-2 border-amber-400">
                      <p className="text-gray-600 text-xs sm:text-sm font-medium">Peso Médio</p>
                      <p className="text-2xl sm:text-4xl font-bold text-primary my-2 sm:my-3">{currentData.peso.medio}kg</p>
                      <p className="text-xs sm:text-sm text-gray-600">variação: {currentData.peso.minimo}kg a {currentData.peso.maximo}kg</p>
                    </div>
                    <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-3 sm:p-4 text-center shadow-sm border-2 border-amber-400">
                      <p className="text-gray-600 text-xs sm:text-sm font-medium">IMC Médio</p>
                      <p className="text-2xl sm:text-4xl font-bold text-primary my-2">{currentData.imc.medio}</p>
                      <p className="text-xs sm:text-sm text-gray-600">variação: {currentData.imc.minimo} a {currentData.imc.maximo}</p>
                    </div>

                    <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-3 sm:p-4 text-center shadow-sm border-2 border-red-500">
                      <p className="text-gray-600 text-xs sm:text-sm font-medium">Acima do Peso Ideal</p>
                      <p className="text-2xl sm:text-4xl font-bold text-red-600 my-2">{percentualAcimaDoPesoIdeal}%</p>
                      <p className="text-xs sm:text-sm text-gray-600">da população que respondeu</p>
                    </div>
                  </div>
                  
                  {/* Card de Peso Crítico quando disponível */}
                  {currentData.peso.pesoCritico && (
                    <div className="mt-4 sm:mt-6">
                      <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-3 sm:p-4 text-center shadow-sm border-2 border-red-500">
                        <p className="text-gray-600 text-xs sm:text-sm font-medium">{currentData.peso.pesoCritico.descricao}</p>
                        <p className="text-2xl sm:text-4xl font-bold text-red-600 my-2">{currentData.peso.pesoCritico.valor}</p>
                        <p className="text-xs sm:text-sm text-gray-600">pacientes identificados</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Informações extras apenas para impressão */}
                <div className="hidden print:block mt-6 text-xs text-gray-600">
                  <p><strong>Peso médio:</strong> Média aritmética do peso corporal de todos os participantes.</p>
                  <p><strong>IMC médio:</strong> Índice de Massa Corporal - peso(kg) ÷ altura²(m).</p>
                  <p><strong>Acima do peso ideal:</strong> Percentual com IMC ≥ 25 (sobrepeso + obesidade).</p>
                </div>
              </div>
            </div>
          </FadeInSection>

          {/* Classificação por IMC */}
          <FadeInSection delay={200}>
            <div className="grid grid-cols-1 gap-6 mb-6">
              <div className="bg-white rounded-slimpass shadow-md p-4 sm:p-6 print-page-break">


                <div className="mb-4 sm:mb-6">
                  <h2 className="text-lg sm:text-xl font-semibold text-gray-800 header-accent mb-2">Classificação por IMC</h2>
                  {currentData.imc.baseUtilizada === "validada" && !(operadoraId === 'fups' && activeTab === 'periodo-novo') && (
                    <div className="bg-blue-50 rounded-lg px-3 py-1 text-center mt-2 mb-4 max-w-xs mx-auto">
                      <span className="text-sm font-normal text-blue-800">
                        Base validada: <b>{currentData.respostas.validadas}</b> pessoas
                      </span>
                    </div>
                  )}
                </div>
                  
                <div className="w-full">
                  {/* Gráfico de IMC - Ocupando toda a largura */}
                  <div className="h-[22rem] sm:h-[28rem] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={imcPieData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          innerRadius={40}
                          outerRadius={90}
                          fill="#8884d8"
                          dataKey="value"
                          label={({ name, value, percent }) => `${(percent * 100).toFixed(1).replace('.', ',')}% (${value})`}
                        >
                                                     {imcPieData.map((entry: PieDataItem, index: number) => {
                             // Determinar cor baseada no nome da categoria
                             const getColor = (name: string) => {
                               if (name.toLowerCase().includes('baixo peso') || name.toLowerCase().includes('< 18')) {
                                 return '#5067c1'; // Azul
                               } else if (name.toLowerCase().includes('peso normal') || name.toLowerCase().includes('18-25')) {
                                 return '#57b85b'; // Verde
                               } else if (name.toLowerCase().includes('sobrepeso') || name.toLowerCase().includes('25-30')) {
                                 return '#ffad33'; // Laranja
                               } else if (name.toLowerCase().includes('obesidade') || name.toLowerCase().includes('> 30')) {
                                 return '#f76056'; // Vermelho
                               } else if (name.toLowerCase().includes('≥ 27') || name.toLowerCase().includes('>= 27')) {
                                 return '#9c27b0'; // Roxo para critério especial
                               }
                               return '#cccccc'; // Cinza padrão
                             };
                             
                             return (
                               <Cell key={`cell-${index}`} fill={getColor(entry.name)} />
                             );
                           })}
                        </Pie>
                        <Tooltip formatter={(value, name) => [`${value} pessoas (${((value as number) / (currentData.respostas.validadas || currentData.respostas.total || currentData.respostas) * 100).toFixed(1).replace('.', ',')}%)`, name]} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                
                {/* Card de Obesidade Unificado com os dados */}
                <div className="mt-10 w-full">
                  <div className="bg-gray-100 rounded-lg shadow-md overflow-hidden">
                    {/* Cabeçalho destacado */}
                      <div className="bg-red-600 p-3 text-white text-center">
                        <h3 className="text-xl font-bold">Obesidade (IMC &gt; 30)</h3>
                    </div>
                    
                    <div className="p-6">
                      <div className="mb-6">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                          <div className="mb-4 md:mb-0">
                            <div className="mt-2 flex flex-col sm:flex-row sm:items-center">
                                <span className="text-2xl sm:text-4xl font-bold text-red-600 mr-3">
                                {typeof currentData.imc.categorias[3]?.percentual === 'number' 
                                  ? `${currentData.imc.categorias[3].percentual.toFixed(1).replace('.', ',')}` 
                                  : typeof currentData.imc.categorias[2]?.percentual === 'number' && currentData.imc.categorias[2].nome.includes('Obesidade')
                                    ? `${currentData.imc.categorias[2].percentual.toFixed(1).replace('.', ',')}`
                                    : (currentData.respostas?.validadas || currentData.respostas) > 0 
                                      ? (imcPieData[3]?.value / (currentData.respostas?.validadas || currentData.respostas) * 100).toFixed(1).replace('.', ',')
                                      : '0,0'}%
                              </span>
                              <p className="text-gray-600">
                              Dos <span className="font-bold text-red-600">
                                {currentData.imc.categorias[3]?.valor || 
                                 (currentData.imc.categorias[2]?.nome.includes('Obesidade') ? currentData.imc.categorias[2].valor : 
                                  currentData.imc.obesidadeGraus?.reduce((acc: number, cur: CategoriaItem) => acc + cur.valor, 0))} pacientes
                              </span> com obesidade,
                              a distribuição por graus é:
                              </p>
                            </div>
                          </div>
                          
                          
                        </div>
                      </div>
                      
                      <hr className="border-gray-200 my-4" />
                      
                      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-5 mt-6">
                        {currentData.imc.obesidadeGraus.map((grau: CategoriaItem, index: number) => (
                          <div key={index} className="bg-white rounded-lg p-3 sm:p-5 shadow-sm border-2 border-red-300 break-words">
                            <h3 className="font-semibold text-gray-800 text-xs sm:text-base">{grau.nome}</h3>
                            <div className="mt-2 sm:mt-3">
                                <span className="text-xl sm:text-3xl font-bold text-red-600">{grau.percentual}%</span>
                              <p className="text-xs sm:text-sm text-gray-600 mt-1 sm:mt-2">{grau.valor} pessoas</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </FadeInSection>

          {/* Perfil de Saúde - Comorbidades */}
          <FadeInSection delay={280}>
            <div className="bg-white rounded-slimpass shadow-md p-4 sm:p-6 mb-6 print-page-break">
              <div className="mb-4 sm:mb-6">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-800 header-accent mb-2">Comorbidades</h2>
                {currentData.comorbidades.baseUtilizada && !(operadoraId === 'fups' && activeTab === 'periodo-novo') && (
                  <div className="bg-blue-50 rounded-lg px-3 py-1 text-center mt-2 mb-4 max-w-xs mx-auto">
                    <span className="text-sm font-normal text-blue-800">
                      {currentData.comorbidades.baseUtilizada === "validada" 
                        ? <>Base validada: <b>{currentData.respostas.validadas}</b> pessoas</>
                        : <>Base completa: <b>{currentData.respostas.total}</b> pessoas</>}
                    </span>
                  </div>
                )}
              </div>
                  
              {/* Presença de Comorbidades */}
              <div className="w-full">
                <div className="h-72 sm:h-96 w-full">
                      <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={comorbidadesPieData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        innerRadius={40}
                        outerRadius={90}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ value, percent }) => `${(percent * 100).toFixed(1).replace('.', ',')}% (${value})`}
                      >
                        {comorbidadesPieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={index === 0 ? '#ffb84d' : '#57b85b'} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value, name) => [`${value} pessoas (${((value as number) / totalRespostas * 100).toFixed(1).replace('.', ',')}%)`, name]} />
                      <Legend />
                    </PieChart>
                      </ResponsiveContainer>
                    </div>
                    
                {/* Tipos de Comorbidades */}
                {currentData.comorbidades.grupos && currentData.comorbidades.grupos.length > 0 && (
                  <div className="mt-10">
                    <h3 className="text-2xl font-semibold text-gray-800 mb-6">Tipos de Comorbidades</h3>
                    
                    {/* Renderização específica por grupo */}
                    <div>
                      {/* 1. Grupo Saúde Mental */}
                      {(() => {
                        const grupoSaudeMental = currentData.comorbidades.grupos.find((grupo: GrupoComorbidade) => grupo.id === 'saude_mental');
                        if (!grupoSaudeMental) return null;
                        
                        const grupoDataSaudeMental = sortedComorbidadesData
                          .filter((item: ComorbidadeItem) => item.grupoId === grupoSaudeMental.id)
                          .map((item: ComorbidadeItem) => ({
                            nome: item.nome,
                            quantidade: item.valor,
                            percentagem: item.percentual
                          }));
                        
                        return (
                          <ComorbidadesChart
                            key={grupoSaudeMental.id}
                            data={grupoDataSaudeMental}
                            title={grupoSaudeMental.nome}
                            colorGroup={COLORS.chart}
                          />
                        );
                      })()}
                      
                      {/* 2. Grupo Doenças Metabólicas e Endócrinas */}
                      {(() => {
                        const grupoMetabolicas = currentData.comorbidades.grupos.find((grupo: GrupoComorbidade) => grupo.id === 'metabolicas');
                        if (!grupoMetabolicas) return null;
                        
                        const grupoDataMetabolicas = sortedComorbidadesData
                          .filter((item: ComorbidadeItem) => item.grupoId === grupoMetabolicas.id)
                          .map((item: ComorbidadeItem) => ({
                            nome: item.nome,
                            quantidade: item.valor,
                            percentagem: item.percentual
                          }));
                        
                        return (
                          <ComorbidadesChart
                            key={grupoMetabolicas.id}
                            data={grupoDataMetabolicas}
                            title={grupoMetabolicas.nome}
                            colorGroup={COLORS.chart}
                          />
                        );
                      })()}
                      
                      {/* 3. Seção especial para Diabetes tipo 2 - FUPS período novo e CABERGS - aparece após doenças metabólicas */}
                      {((operadoraId === 'fups' && activeTab === 'periodo-novo') || operadoraId === 'cabergs') && currentData.comorbidades.diabetesTipo2PorIMC && (
                        <div className="mt-10 w-full">
                          <div className="bg-gray-100 rounded-lg shadow-md overflow-hidden">
                            {/* Cabeçalho destacado */}
                            <div className="bg-red-600 p-3 text-white text-center">
                              <h3 className="text-xl font-bold">Diabetes Tipo 2 - Distribuição por IMC</h3>
                            </div>
                            
                            <div className="p-6">
                              <div className="mb-6">
                                <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                                  <div className="mb-4 md:mb-0">
                                    <div className="mt-2 flex items-center">
                                      <span className="text-4xl font-bold text-red-600 mr-3">
                                        {currentData.comorbidades.diabetesTipo2PorIMC.percentualTotal}%
                                      </span>
                                      <p className="text-gray-600">
                                        <b>{currentData.comorbidades.diabetesTipo2PorIMC.total} pacientes</b> com diabetes tipo 2, a distribuição por IMC é:
                                      </p>
                                    </div>
                                  </div>
                                  

                                </div>
                              </div>
                              
                              <hr className="border-gray-200 my-4" />
                              
                              {/* Cards de distribuição por IMC - seguindo padrão dos Dados Gerais */}
                              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5 mt-6 overflow-hidden">
                                {currentData.comorbidades.diabetesTipo2PorIMC.distribuicaoPorIMC.map((faixa: any, index: number) => {
                                  const getCardStyle = (risco: string) => {
                                    switch (risco) {
                                      case 'alto': 
                                        return {
                                          bg: 'bg-gradient-to-br from-red-50 to-red-100',
                                          border: 'border-red-500',
                                          textColor: 'text-red-600'
                                        };
                                      case 'moderado': 
                                        return {
                                          bg: 'bg-gradient-to-br from-yellow-50 to-yellow-100',
                                          border: 'border-yellow-500',
                                          textColor: 'text-yellow-600'
                                        };
                                      case 'baixo': 
                                        return {
                                          bg: 'bg-gradient-to-br from-green-50 to-green-100',
                                          border: 'border-green-500',
                                          textColor: 'text-green-600'
                                        };
                                      default: 
                                        return {
                                          bg: 'bg-gradient-to-br from-gray-50 to-gray-100',
                                          border: 'border-gray-500',
                                          textColor: 'text-gray-600'
                                        };
                                    }
                                  };
                                  
                                  const cardStyle = getCardStyle(faixa.corRisco);
                                  
                                  return (
                                    <div key={index} className={`${cardStyle.bg} rounded-lg p-5 text-center shadow-sm border-2 ${cardStyle.border} break-words`}>
                                      <h3 className="font-semibold text-gray-800">{faixa.faixa}</h3>
                                      <div className="mt-3">
                                        <span className={`text-4xl font-bold ${cardStyle.textColor}`}>{faixa.percentual}%</span>
                                        <p className="text-sm text-gray-600 mt-2">{faixa.valor} pessoas</p>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                              
                              {/* Informação clínica adicional */}
                              <div className="mt-6 bg-red-50 rounded-lg p-4 border-2 border-red-300">
                                <div className="text-center">
                                  <h4 className="font-bold text-red-800 mb-2">⚠️ Alto risco cardiovascular: <span className="text-sm text-gray-600 font-medium">pacientes diabéticos com <strong>IMC ≥ 30</strong> necessitam acompanhamento especializado prioritário.</span></h4>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* 4. Grupo Outros */}
                      {(() => {
                        const grupoOutros = currentData.comorbidades.grupos.find((grupo: GrupoComorbidade) => grupo.id === 'outros');
                        if (!grupoOutros) return null;
                        
                        const grupoDataOutros = sortedComorbidadesData
                          .filter((item: ComorbidadeItem) => item.grupoId === grupoOutros.id)
                          .map((item: ComorbidadeItem) => ({
                            nome: item.nome,
                            quantidade: item.valor,
                            percentagem: item.percentual
                          }));
                        
                        return (
                          <div className="mt-10">
                            <ComorbidadesChart
                              key={grupoOutros.id}
                              data={grupoDataOutros}
                              title={grupoOutros.nome}
                              colorGroup={COLORS.chart}
                            />
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </FadeInSection>

          {/* Sono & Hidratação */}
          <FadeInSection delay={340}>
            <div className="grid grid-cols-1 gap-6 sm:gap-8 mb-6 sm:mb-8">
              {/* Sono */}
              <div className="bg-white rounded-slimpass shadow-md p-4 sm:p-8">
                <div className="mb-4 sm:mb-6">
                  <h2 className="text-lg sm:text-xl font-semibold text-gray-800 header-accent mb-2">Sono</h2>
                  {currentData.sono.baseUtilizada && !(operadoraId === 'fups' && activeTab === 'periodo-novo') && (
                    <div className="bg-blue-50 rounded-lg px-3 py-1 text-center mt-2 mb-4 max-w-xs mx-auto">
                      <span className="text-sm font-normal text-blue-800">
                        {currentData.sono.baseUtilizada === "validada" 
                          ? <>Base validada: <b>{currentData.respostas.validadas}</b> pessoas</>
                          : <>Base completa: <b>{currentData.respostas.total}</b> pessoas</>}
                      </span>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6">
                  {[
                    { title: "Não reparador", percent: currentData.sono.percentualNaoReparador, value: currentData.sono.naoReparador, bgColor: "from-orange-50 to-orange-100", borderColor: "border-orange-400", textColor: "text-orange-500" },
                    { title: "Menos de 7 horas", percent: currentData.sono.percentualMenosDeSeteHoras, value: currentData.sono.menosDeSeteHoras, bgColor: "from-red-50 to-red-100", borderColor: "border-red-400", textColor: "text-red-500" },
                    { title: "Ronco Noturno", percent: currentData.sono.percentualRoncoNoturno, value: currentData.sono.roncoNoturno, bgColor: "from-yellow-50 to-yellow-100", borderColor: "border-yellow-400", textColor: "text-yellow-600" },
                    { title: "Insônia", percent: currentData.sono.percentualInsonia, value: currentData.sono.insonia, bgColor: "from-purple-50 to-purple-100", borderColor: "border-purple-400", textColor: "text-purple-600" }
                  ].sort((a, b) => b.percent - a.percent).map((item, index) => (
                    <div key={index} className={`bg-gradient-to-br ${item.bgColor} rounded-lg p-3 sm:p-5 text-center shadow-sm border-2 ${item.borderColor}`}>
                      <p className="text-gray-600 text-xs sm:text-sm font-medium">{item.title}</p>
                      <p className={`text-lg sm:text-2xl font-bold ${item.textColor} mt-1 sm:mt-2`}>{item.percent.toFixed(1).replace('.', ',')}%</p>
                      <p className="text-xs sm:text-sm text-gray-600 mt-1">{item.value} pessoas</p>
                    </div>
                  ))}
                </div>

                {/* Informações extras apenas para impressão */}
                <div className="hidden print:block mt-6 text-xs text-gray-600">
                  <p><strong>Sono não reparador:</strong> Pacientes que relatam acordar cansados mesmo após dormir.</p>
                  <p><strong>Menos de 7 horas:</strong> Duração de sono abaixo do recomendado para adultos.</p>
                  <p><strong>Ronco noturno:</strong> Pacientes que relatam roncar durante o sono.</p>
                  <p><strong>Insônia:</strong> Dificuldade para adormecer ou manter o sono.</p>
                </div>
              </div>

              {/* Hidratação */}
              <div className="bg-white rounded-slimpass shadow-md p-4 sm:p-8">
                <div className="mb-4 sm:mb-6">
                  <h2 className="text-lg sm:text-xl font-semibold text-gray-800 header-accent mb-2">Hidratação por Dia</h2>
                </div>

                {/* Determinar formato dos dados de hidratação */}
                {(() => {
                  const hidratacaoArr: HidratacaoItem[] = Array.isArray(currentData.hidratacao)
                    ? currentData.hidratacao
                    : (currentData.hidratacao?.faixas || []);

                  // Função para ordenar por volume crescente
                  const sortByVolume = (a: HidratacaoItem, b: HidratacaoItem) => {
                    const getVolumeOrder = (faixa: string) => {
                      if (faixa.includes('< 500ml') || faixa.includes('<500ml')) return 1;
                      if (faixa.includes('500ml - 1000ml') || faixa.includes('500-1000')) return 2;
                      if (faixa.includes('1000ml - 2000ml') || faixa.includes('1000-2000')) return 3;
                      if (faixa.includes('> 2000ml') || faixa.includes('>2000') || faixa.includes('ideal')) return 4;
                      return 5; // fallback
                    };
                    return getVolumeOrder(a.faixa) - getVolumeOrder(b.faixa);
                  };

                  return (
                    <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6">
                      {hidratacaoArr.sort(sortByVolume).map((item: HidratacaoItem, index: number) => (
                        <div key={index} className={`bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-3 sm:p-5 text-center shadow-sm border-2 border-blue-400`}>
                          <p className="text-gray-600 text-xs sm:text-sm font-medium break-words">{item.faixa}</p>
                          <p className="text-lg sm:text-2xl font-bold text-blue-600 mt-1 sm:mt-2">{item.percentual.toFixed(1).replace('.', ',')}%</p>
                          <p className="text-xs sm:text-sm text-gray-600 mt-1">{item.valor} pessoas</p>
                        </div>
                      ))}
                    </div>
                  );
                })()}

                {/* Informações extras apenas para impressão */}
                <div className="hidden print:block mt-6 text-xs text-gray-600">
                  <p><strong>Hidratação adequada:</strong> Recomenda-se o consumo de pelo menos 2 litros de água por dia.</p>
                  <p><strong>Consumo baixo (&lt;1L):</strong> Pode indicar risco de desidratação e problemas renais.</p>
                  <p><strong>Consumo ideal (&gt;2L):</strong> Contribui para melhor funcionamento dos órgãos e metabolismo.</p>
                </div>
              </div>
            </div>
          </FadeInSection>

          {/* Sedentarismo e Alimentação */}
          <FadeInSection delay={400}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 mb-6 sm:mb-8">
              {/* Sedentarismo */}
              <div className="bg-white rounded-slimpass shadow-md p-4 sm:p-8">
                <div className="mb-4 sm:mb-6">
                  <h2 className="text-lg sm:text-xl font-semibold text-gray-800 header-accent mb-2">Sedentarismo</h2>
                  {currentData.sedentarismo.baseUtilizada && !(operadoraId === 'fups' && activeTab === 'periodo-novo') && (
                    <div className="bg-blue-50 rounded-lg px-3 py-1 text-center mt-2 mb-4 max-w-xs mx-auto">
                      <span className="text-sm font-normal text-blue-800">
                        {currentData.sedentarismo.baseUtilizada === "validada" 
                          ? <>Base validada: <b>{currentData.respostas.validadas}</b> pessoas</>
                          : <>Base completa: <b>{currentData.respostas.total}</b> pessoas</>}
                      </span>
                    </div>
                  )}
                </div>
              
                <div className="text-left mb-6">
                  <span className="font-bold text-xl text-red-500">{currentData.sedentarismo.percentual.toFixed(1).toString().replace('.', ',')}%</span> dos pacientes são sedentários
                  {currentData.sedentarismo.descricao && (
                    <p className="text-sm text-gray-600 mt-2">({currentData.sedentarismo.descricao})</p>
                  )}
                  {currentData.sedentarismo.mediaExerciciosPorSemana && (
                    <p className="text-sm text-blue-600 mt-2 font-medium">
                      Média de exercícios: {currentData.sedentarismo.mediaExerciciosPorSemana.toFixed(2).replace('.', ',')}x por semana
                    </p>
                  )}
                </div>

                <div className="h-[240px] sm:h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={sedentarismoData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        innerRadius={30}
                        outerRadius={60}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ value, percent }) => `${(percent * 100).toFixed(1).replace('.', ',')}% (${value})`}
                      >
                        <Cell fill="#f76056" />
                        <Cell fill="#57b85b" />
                      </Pie>
                      <Tooltip formatter={(value, name) => [`${value} pessoas (${((value as number) / totalRespostas * 100).toFixed(1).replace('.', ',')}%)`, name]} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            
              {/* Alimentação */}
              <div className="bg-white rounded-slimpass shadow-md p-4 sm:p-8">
                <div className="mb-4 sm:mb-6">
                  <h2 className="text-lg sm:text-xl font-semibold text-gray-800 header-accent mb-2">Hábitos Alimentares</h2>
                  {currentData.alimentacao.baseUtilizada && !(operadoraId === 'fups' && activeTab === 'periodo-novo') && (
                    <div className="bg-blue-50 rounded-lg px-3 py-1 text-center mt-2 mb-4 max-w-xs mx-auto">
                      <span className="text-sm font-normal text-blue-800">
                        {currentData.alimentacao.baseUtilizada === "validada" 
                          ? <>Base validada: <b>{currentData.respostas.validadas}</b> pessoas</>
                          : <>Base completa: <b>{currentData.respostas.total}</b> pessoas</>}
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex flex-col space-y-4">
                  <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border-2 border-green-600 text-center shadow-sm">
                    <p className="text-sm text-gray-600 text-center">Média de refeições por dia</p>
                    <p className="text-3xl font-bold text-green-700 mt-1 text-center">{currentData.alimentacao.mediaRefeicoes.toFixed(1).replace('.', ',')}</p>
                  </div>
                
                  <div className="flex flex-col space-y-4">
                    <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-4 text-center shadow-sm border-2 border-amber-400">
                      <p className="text-gray-600 text-sm font-medium">5-6 refeições por dia</p>
                      <p className="text-xl font-bold text-amber-700 mt-2">{currentData.alimentacao.percentualCincoOuSeisPorDia.toFixed(1).replace('.', ',')}%</p>
                      <p className="text-sm text-gray-600 mt-1">{currentData.alimentacao.cincoOuSeisPorDia} pessoas</p>
                    </div>
                    
                    <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4 text-center shadow-sm border-2 border-red-400">
                      <p className="text-gray-600 text-sm font-medium">Qualidade pode melhorar</p>
                      <p className="text-xl font-bold text-red-700 mt-2">{currentData.alimentacao.percentualQualidadePodeMelhorar.toFixed(1).replace('.', ',')}%</p>
                      <p className="text-sm text-gray-600 mt-1">{currentData.alimentacao.qualidadePodeMelhorar} pessoas</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </FadeInSection>

          {/* Footer com metadata */}
          {currentData.metadata && (
            <FadeInSection delay={500}>
              <div className="bg-white rounded-slimpass shadow-md p-5 mb-8 text-center">
                <p className="text-xs text-gray-500">
                  Atualizado em {currentData.metadata.dataAtualizacao} • Versão {currentData.metadata.versaoDados}
              </p>
            </div>
            </FadeInSection>
          )}
        </div>
      </div>
      
      {/* Rodapé com copyright - fora do container principal para ocupar toda a largura */}
      <footer className="w-full py-6 bg-gray-100 border-t border-gray-200">
        <div className="flex flex-col items-center">
          <img src={slimpassLogoUrl} alt="SlimPass" className="h-12 mb-4" />
          <p className="text-gray-500 text-sm">© SlimPass {new Date().getFullYear()}. Todos os direitos reservados.</p>
        </div>
      </footer>
    </div>
  );
};

export default Dashboard;
